import { useTranslation } from '@rhino-project/core/i18n';
import { ChangeEvent, ComponentProps, use, useMemo } from 'react';

import { ModelCreateModal, ModelFieldGroup } from '@rhino-project/ui-heroui';
import {
  Alert,
  AlertContent,
  AlertTitle,
  Label,
  Link,
  Radio,
  RadioControl,
  RadioIndicator,
  RadioContent,
  TextField,
  Input,
  FieldError
} from '@heroui/react';

import templateRoster from '../../assets/espm_roster_template_v1.xlsx';
import { useFieldError } from '@rhino-project/core/hooks';
import { useController, useWatch } from 'react-hook-form';
import TeamContext from '../../contexts/TeamContext';
import {
  useActiveSeasons,
  useCurrentSeason,
  useTeamSeasons
} from '../../hooks/ots';
import FieldRadioButton from '../forms/fields/FieldRadioButton';

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let encoded = (reader.result as string).replace(/^data:(.*,)?/, '');
      if (encoded.length % 4 > 0) {
        encoded += '='.repeat(4 - (encoded.length % 4));
      }
      resolve(encoded);
    };
    reader.onerror = (error) => reject(error);
  });

type FieldVariant = 'primary' | 'secondary';

interface SeasonIsProps {
  team: { name: string } | null;
  variant?: FieldVariant;
}

const SeasonIs = ({ team, variant }: SeasonIsProps) => {
  const { t } = useTranslation();

  return (
    <FieldRadioButton
      label={t('frontend.messages.season_is')}
      path="status"
      variant={variant}
    >
      <Radio value="planning">
        <RadioContent>
          <RadioControl>
            <RadioIndicator />
          </RadioControl>
          {t('frontend.messages.future')}{' '}
          <em>{t('frontend.messages.future_team', { team: team?.name })}</em>
        </RadioContent>
      </Radio>
      <Radio value="active">
        <RadioContent>
          <RadioControl>
            <RadioIndicator />
          </RadioControl>
          {t('frontend.messages.current')}{' '}
          <em>{t('frontend.messages.current_team', { team: team?.name })}</em>
        </RadioContent>
      </Radio>
    </FieldRadioButton>
  );
};

const SeasonStartWithFile = ({ variant }: { variant?: FieldVariant }) => {
  const { t } = useTranslation();

  const startWith = useWatch({ name: 'start_with' });
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    field: { ref, value, onChange, disabled: _disabled, ...fieldProps },
    fieldState: { error }
  } = useController({
    name: 'start_with_file'
  });

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.[0]) return;
    const encoded = await toBase64(files[0]);

    onChange(encoded);
  };

  return (
    <div className="m-2">
      <TextField
        isDisabled={startWith !== 'import'}
        isInvalid={!!error}
        variant={variant}
      >
        <Label>{t('frontend.messages.select_espm_team_template')}</Label>
        <Input
          id="start_with_file"
          type="file"
          {...fieldProps}
          ref={ref}
          onChange={handleFileChange}
        />
        <FieldError>{error?.message}</FieldError>
      </TextField>
      <Link className="ml-2 mt-2 inline-block" href={templateRoster}>
        {t('frontend.messages.download_the_template')}
      </Link>
    </div>
  );
};

interface SeasonStartWithProps {
  activeTeamSeason: { season: { name?: string | null } } | null | undefined;
  variant?: FieldVariant;
}

const SeasonStartWith = ({
  activeTeamSeason,
  variant
}: SeasonStartWithProps) => {
  const { t } = useTranslation();

  return (
    <>
      <FieldRadioButton
        label={t('frontend.messages.start_with')}
        path="start_with"
        variant={variant}
      >
        <Radio value="none">
          <RadioContent>
            <RadioControl>
              <RadioIndicator />
            </RadioControl>
            {t('frontend.messages.no_players')}
          </RadioContent>
        </Radio>
        {activeTeamSeason && (
          <Radio value="transfer">
            <RadioContent>
              <RadioControl>
                <RadioIndicator />
              </RadioControl>
              {t('frontend.messages.players_from_season', {
                season: activeTeamSeason.season.name
              })}
            </RadioContent>
          </Radio>
        )}
        <Radio value="import">
          <RadioContent>
            <RadioControl>
              <RadioIndicator />
            </RadioControl>
            {t('frontend.messages.imported_spreadsheet')}
          </RadioContent>
        </Radio>
      </FieldRadioButton>

      <SeasonStartWithFile variant={variant} />
    </>
  );
};
const SeasonImportErrors = () => {
  const error = useFieldError('import.0');

  if (!error) return null;

  return (
    <Alert status="danger">
      <AlertContent>
        <AlertTitle>{String(error)}</AlertTitle>
      </AlertContent>
    </Alert>
  );
};

const SeasonSelect = ({ variant }: { variant?: FieldVariant }) => {
  // Only offer seasons the team can actually add: drop seasons before the
  // current one (you don't plan into the past) and seasons the team already
  // has (uniqueness is otherwise only caught at save). List ascending so the
  // nearest upcoming season is first.
  const currentSeason = useCurrentSeason() as { starts_on: string } | null;
  const { results: teamSeasons } = useTeamSeasons();

  const filter = useMemo(() => {
    const ownedSeasonIds = (
      (teamSeasons as { season: { id: number } }[] | undefined) ?? []
    ).map((ts) => ts.season.id);

    return {
      ...(currentSeason && {
        starts_on: { gteq: currentSeason.starts_on }
      }),
      ...(ownedSeasonIds.length > 0 && { id: { diff: ownedSeasonIds } })
    };
  }, [currentSeason, teamSeasons]);

  return (
    <ModelFieldGroup
      path="season"
      filter={filter}
      order={['season.starts_on']}
      variant={variant}
    />
  );
};

const SeasonSidebarForm = (props: ComponentProps<typeof ModelCreateModal>) => {
  const { team } = use(TeamContext);

  const { results } = useActiveSeasons();
  const activeTeamSeason = useMemo(() => results?.[0], [results]);

  const paths = useMemo(() => {
    return [
      // eslint-disable-next-line @eslint-react/no-missing-key
      <SeasonSelect />,
      // eslint-disable-next-line @eslint-react/no-missing-key
      <SeasonIs team={team} />,
      // eslint-disable-next-line @eslint-react/no-missing-key
      <SeasonStartWith activeTeamSeason={activeTeamSeason} />,
      // eslint-disable-next-line @eslint-react/no-missing-key
      <SeasonImportErrors />
    ];
  }, [team, activeTeamSeason]);

  return (
    <ModelCreateModal
      {...props}
      model="team_season"
      parentId={team?.id}
      paths={paths}
      extraDefaultValues={{
        start_with: 'none',
        status: 'planning',
        start_with_file: null
      }}
    />
  );
};

export default SeasonSidebarForm;
